import * as actions from './actions';
import * as buildingActions from 'store/building/actions';
import { Observable, of, from } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { Epic } from 'redux-observable';
import { RootAction } from 'store/actions';
import { RootDependencies } from 'store/dependencies';
import { RootState } from 'store/reducer';
import { isActionOf } from 'typesafe-actions';
import { mapServiceProviders } from 'store/serviceProviders/mappers';
import { ServiceProviderV3 } from 'store/serviceProviders/types';
import { selectFeatureFlags } from 'store/featureFlags/selectors';

export const getServiceProvidersEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getServiceProviders.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]): Observable<RootAction> => {
      const { ownerType, ownerUuid } = payload;
      const { serviceBookingUsingV3Endpoints } = selectFeatureFlags(state);
      const client = apiClient(state);
      const request = serviceBookingUsingV3Endpoints
        ? client.getServiceProvidersV3(ownerType, ownerUuid)
        : client.getServiceProviders(ownerType, ownerUuid);

      return request.pipe(
        mergeMap(({ response }) => {
          const providers = mapServiceProviders(response.data.service_providers);
          const buildingAction = response.data.building_context
            ? buildingActions.setServiceBuildingFromContext(response.data.building_context)
            : buildingActions.clearServiceBuilding();
          return from([actions.getServiceProviders.success(providers), buildingAction]);
        }),
        catchError(error => of(actions.getServiceProviders.failure({ error }))),
      );
    }),
  );

export const getCurrentServiceProviderEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getCurrentServiceProvider.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]): Observable<RootAction> => {
      const { ownerType, ownerUuid, companyUuid } = payload;
      return apiClient(state)
        .getCurrentServiceProviderV3(ownerType, ownerUuid, companyUuid)
        .pipe(
          map(xhrPayload => {
            return actions.getCurrentServiceProvider.success(
              xhrPayload.response.data.service_provider as ServiceProviderV3,
            );
          }),
          catchError(error => of(actions.getCurrentServiceProvider.failure({ error }))),
        );
    }),
  );
